import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { useSetupStatus } from "@/lib/auth";
import { Navigate } from "react-router-dom";

export function Login() {
  const setup = useSetupStatus();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.post<{ user: { id: string; email: string } }>("/auth/login", { email, password }),
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      const r = await m.mutateAsync();
      // Seed the ["me"] cache directly with the response so RequireAuth sees
      // the new user immediately — invalidateQueries doesn't await the refetch,
      // which caused the first-login redirect to bounce back to /login.
      qc.setQueryData(["me"], r.user);
      // 007 — honor ?next= (used by /api/v1/protection/authorize for
      // platform-auth protected deployments). Same-origin paths only.
      const next = new URLSearchParams(loc.search).get("next");
      if (next && next.startsWith("/") && !next.startsWith("//")) {
        window.location.href = next; // API routes need a full navigation, not SPA nav
        return;
      }
      const from = (loc.state as { from?: string } | null)?.from ?? "/";
      nav(from, { replace: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Sign-in failed");
    }
  }

  if (setup.data && !setup.data.seeded) return <Navigate to="/setup" replace />;

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-4xl font-display italic">openfront</div>
          <div className="text-sm text-muted-foreground mt-2">Sign in to your platform</div>
        </div>
        <form onSubmit={onSubmit} className="of-card p-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
            </div>
            <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {err && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{err}</div>}
          <Button variant="primary" size="lg" type="submit" className="w-full" disabled={m.isPending}>
            {m.isPending ? "Signing in…" : <>Sign in <ArrowRight className="h-3.5 w-3.5" /></>}
          </Button>
        </form>
        <div className="text-2xs text-subtle-foreground text-center mt-4">
          No public signup. Ask your administrator for an{" "}
          <Link to="/invite" className="text-accent hover:underline">invite link</Link>.
        </div>
      </div>
    </div>
  );
}
